/**
 * TalentSelector - Hierarkisk talent-tre med checkboxes
 * 
 * Brukes for å velge talenter og talentgrupper for oppmøtetider.
 * Støtter 3-nivå hierarki med visuelt skille mellom grupper og enkelttalenter.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { talentAPI, Talent, TalentKategori } from '@/services/api';
import { cn } from '@/lib/utils';

export interface SelectedTalent {
  id: number;
  navn: string;
  erKategori: boolean;
  kategoriSti?: string;
}

const STI_SKILLE = ' → ';

/**
 * Er element X dekket av kategorien K? (X != K og X ligger i K eller under.)
 * Kategorier dekkes bare av strengt dypere stier; talenter også av kategorien de ligger direkte i.
 */
const erDekketAv = (x: SelectedTalent, k: SelectedTalent): boolean => {
  if (!k.erKategori || !k.kategoriSti || !x.kategoriSti) return false;
  if (x.erKategori) {
    return x.kategoriSti.startsWith(k.kategoriSti + STI_SKILLE);
  }
  return (
    x.kategoriSti === k.kategoriSti || x.kategoriSti.startsWith(k.kategoriSti + STI_SKILLE)
  );
};

/** Finn den ytterste valgte kategorien som dekker x (eller undefined). */
const finnDekkendeKategori = (
  x: SelectedTalent,
  valgte: SelectedTalent[]
): SelectedTalent | undefined =>
  valgte
    .filter(k => erDekketAv(x, k))
    .sort((a, b) => (a.kategoriSti!.length - b.kategoriSti!.length))[0];

/**
 * Valgt kategori = alle i den. Fjerner talenter og underkategorier som er dekket av en
 * valgt forfar-kategori. Rekkefølgen ellers bevares.
 */
export const fjernDekkedeValg = (valgte: SelectedTalent[]): SelectedTalent[] =>
  valgte.filter(x => !finnDekkendeKategori(x, valgte));

interface TalentSelectorProps {
  selectedTalents: SelectedTalent[];
  onTalentsChange: (talents: SelectedTalent[]) => void;
  allowGroups?: boolean; // true = tillat valg av grupper
}

interface TalentNode {
  id: number;
  navn: string;
  erKategori: boolean;
  kategoriSti: string;
  level: number;
  children: Map<string, TalentNode>;
  talenter: Talent[];
}

/**
 * Bygg hierarkisk tre-struktur fra talenter og kategorier
 */
const buildTalentTree = (
  kategorier: TalentKategori[],
  talenter: Talent[]
): Map<string, TalentNode> => {
  const root = new Map<string, TalentNode>();
  
  // Bygg kategori-struktur
  kategorier.forEach(kategori => {
    const parts = kategori.path?.split(' → ') || [kategori.navn];
    let currentLevel = root;
    let currentPath = '';
    
    parts.forEach((part, index) => {
      currentPath = currentPath ? `${currentPath} → ${part}` : part;
      
      if (!currentLevel.has(part)) {
        currentLevel.set(part, {
          id: kategori.id,
          navn: part,
          erKategori: true,
          kategoriSti: currentPath,
          level: index,
          children: new Map(),
          talenter: [],
        });
      }
      
      const node = currentLevel.get(part)!;
      currentLevel = node.children;
    });
  });
  
  // Legg til talenter i riktig kategori
  talenter.forEach(talent => {
    const kategoriSti = talent.kategori_navn || 'Andre';
    const parts = kategoriSti.split(' → ');
    let currentLevel = root;
    
    parts.forEach((part, index) => {
      if (currentLevel.has(part)) {
        const node = currentLevel.get(part)!;
        if (index === parts.length - 1) {
          // Siste nivå - legg til talent
          node.talenter.push(talent);
        }
        currentLevel = node.children;
      }
    });
  });
  
  return root;
};

/**
 * Rekursiv komponent for å vise talent-node
 */
const TalentNodeComponent: React.FC<{
  node: TalentNode;
  selectedTalents: SelectedTalent[];
  onToggle: (talent: SelectedTalent) => void;
  allowGroups: boolean;
  expandedNodes: Record<string, boolean>;
  onToggleExpand: (path: string) => void;
}> = ({
  node,
  selectedTalents,
  onToggle,
  allowGroups,
  expandedNodes,
  onToggleExpand,
}) => {
  const isExpanded = expandedNodes[node.kategoriSti] ?? true;
  const dekketAv = finnDekkendeKategori(
    { id: node.id, navn: node.navn, erKategori: true, kategoriSti: node.kategoriSti },
    selectedTalents
  );
  const isSelected =
    !dekketAv &&
    selectedTalents.some(
      st =>
        (node.erKategori && st.erKategori && st.kategoriSti === node.kategoriSti) ||
        (!node.erKategori && !st.erKategori && st.id === node.id)
    );
  
  const hasChildren = (node.children && node.children.size > 0) || node.talenter.length > 0;
  
  // Fargeklasser basert på hierarki-nivå
  const getLevelColors = (level: number) => {
    switch (level) {
      case 0:
        return 'bg-blue-50 border-blue-200 hover:bg-blue-100';
      case 1:
        return 'bg-purple-50 border-purple-200 hover:bg-purple-100';
      case 2:
        return 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100';
      default:
        return 'bg-gray-50 border-gray-200 hover:bg-gray-100';
    }
  };
  
  return (
    <div className="space-y-1">
      <div
        className={cn(
          'flex items-center gap-2 p-2 rounded-md border',
          getLevelColors(node.level),
          isSelected && 'ring-2 ring-primary',
          dekketAv && 'opacity-60'
        )}
      >
        {hasChildren && (
          <Collapsible
            open={isExpanded}
            onOpenChange={() => onToggleExpand(node.kategoriSti)}
          >
            <CollapsibleTrigger className="p-0 hover:bg-transparent">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </CollapsibleTrigger>
          </Collapsible>
        )}
        {!hasChildren && <div className="w-4" />}
        
        <Checkbox
          id={`talent-${node.id}-${node.kategoriSti}`}
          checked={isSelected}
          onCheckedChange={() => {
            if (node.erKategori && !allowGroups) {
              return; // Ikke tillat valg av grupper
            }
            onToggle({
              id: node.id,
              navn: node.navn,
              erKategori: node.erKategori,
              kategoriSti: node.erKategori ? node.kategoriSti : undefined,
            });
          }}
          disabled={(node.erKategori && !allowGroups) || !!dekketAv}
        />
        
        <Label
          htmlFor={`talent-${node.id}-${node.kategoriSti}`}
          className={cn(
            'flex-1 cursor-pointer',
            node.erKategori && 'font-semibold',
            node.erKategori && !allowGroups && 'opacity-50 cursor-not-allowed'
          )}
        >
          {node.navn}
        </Label>
        
        {node.erKategori && (
          <Badge variant="outline" className="text-xs">
            Gruppe
          </Badge>
        )}
      </div>
      
      {hasChildren && isExpanded && (
        <div className="ml-6 space-y-1">
          {/* Vis barn-kategorier */}
          {Array.from(node.children.values())
            .sort((a, b) => a.navn.localeCompare(b.navn))
            .map(child => (
              <TalentNodeComponent
                key={`${child.id}-${child.kategoriSti}`}
                node={child}
                selectedTalents={selectedTalents}
                onToggle={onToggle}
                allowGroups={allowGroups}
                expandedNodes={expandedNodes}
                onToggleExpand={onToggleExpand}
              />
            ))}
          
          {/* Vis talenter */}
          {node.talenter
            .sort((a, b) => a.navn.localeCompare(b.navn))
            .map(talent => {
              const talentDekketAv = finnDekkendeKategori(
                {
                  id: talent.id,
                  navn: talent.navn,
                  erKategori: false,
                  kategoriSti: talent.kategori_navn,
                },
                selectedTalents
              );
              const talentValgt =
                !talentDekketAv &&
                selectedTalents.some(st => !st.erKategori && st.id === talent.id);
              return (
                <div
                  key={talent.id}
                  className={cn(
                    'flex items-center gap-2 p-2 rounded-md border bg-white hover:bg-gray-50',
                    talentValgt && 'ring-2 ring-primary',
                    talentDekketAv && 'opacity-60'
                  )}
                >
                  <div className="w-4" />
                  <Checkbox
                    id={`talent-${talent.id}`}
                    checked={talentValgt}
                    disabled={!!talentDekketAv}
                    onCheckedChange={() => {
                      onToggle({
                        id: talent.id,
                        navn: talent.navn,
                        erKategori: false,
                        kategoriSti: talent.kategori_navn,
                      });
                    }}
                  />
                  <Label
                    htmlFor={`talent-${talent.id}`}
                    className="flex-1 cursor-pointer"
                  >
                    {talent.navn}
                  </Label>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export const TalentSelector: React.FC<TalentSelectorProps> = ({
  selectedTalents,
  onTalentsChange,
  allowGroups = true,
}) => {
  const [kategorier, setKategorier] = useState<TalentKategori[]>([]);
  const [talenter, setTalenter] = useState<Talent[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [kategoriData, talentData] = await Promise.all([
          talentAPI.getAllKategorier(),
          talentAPI.getAll(),
        ]);
        setKategorier(kategoriData.kategorier);
        setTalenter(talentData.talenter);
      } catch (error) {
        console.error('Feil ved henting av talenter:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const talentTree = useMemo(() => {
    return buildTalentTree(kategorier, talenter);
  }, [kategorier, talenter]);
  
  // Fyll inn manglende kategoriSti for enkelttalenter (f.eks. lastet fra lagret oppmøte)
  const fullstendigeValg = useMemo(() => {
    const stiPerId = new Map(talenter.map(t => [t.id, t.kategori_navn]));
    return selectedTalents.map(st =>
      !st.erKategori && !st.kategoriSti && stiPerId.get(st.id)
        ? { ...st, kategoriSti: stiPerId.get(st.id) }
        : st
    );
  }, [selectedTalents, talenter]);

  const handleToggle = (talent: SelectedTalent) => {
    const isSelected = fullstendigeValg.some(
      st =>
        (talent.erKategori && st.erKategori && st.kategoriSti === talent.kategoriSti) ||
        (!talent.erKategori && !st.erKategori && st.id === talent.id)
    );
    
    if (isSelected) {
      // Fjern
      onTalentsChange(
        fjernDekkedeValg(
          fullstendigeValg.filter(
            st =>
              !(
                (talent.erKategori && st.erKategori && st.kategoriSti === talent.kategoriSti) ||
                (!talent.erKategori && !st.erKategori && st.id === talent.id)
              )
          )
        )
      );
    } else {
      // Legg til
      onTalentsChange(fjernDekkedeValg([...fullstendigeValg, talent]));
    }
  };
  
  const handleToggleExpand = (path: string) => {
    setExpandedNodes(prev => ({
      ...prev,
      [path]: !(prev[path] ?? true),
    }));
  };
  
  const valgteSynlige = useMemo(() => fjernDekkedeValg(fullstendigeValg), [fullstendigeValg]);

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Laster talenter...</div>;
  }
  
  return (
    <div className="space-y-2 max-h-96 overflow-y-auto border rounded-md p-4">
      {Array.from(talentTree.values())
        .sort((a, b) => a.navn.localeCompare(b.navn))
        .map(node => (
          <TalentNodeComponent
            key={`${node.id}-${node.kategoriSti}`}
            node={node}
            selectedTalents={fullstendigeValg}
            onToggle={handleToggle}
            allowGroups={allowGroups}
            expandedNodes={expandedNodes}
            onToggleExpand={handleToggleExpand}
          />
        ))}
      
      {valgteSynlige.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <div className="text-sm font-semibold mb-2">Valgte talenter:</div>
          <div className="flex flex-wrap gap-2">
            {valgteSynlige.map((st, idx) => (
              <Badge key={idx} variant="secondary">
                {st.navn}
                {st.erKategori && ' (gruppe)'}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

